import { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api';

const SID_KEY = 'pkp_presence_sid';
const HEARTBEAT_MS = 15000; // ping every 15s
const REFRESH_MS = 12000;   // refresh peers' count every 12s

const getOrCreateSid = () => {
  try {
    let sid = localStorage.getItem(SID_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `sid-${Math.random().toString(36).slice(2)}-${Date.now()}`);
      localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return `sid-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }
};

/**
 * Sends a presence heartbeat while the tab is visible, and returns the
 * live listener count refreshed periodically. Skips heartbeats when the
 * tab is hidden so idle background tabs don't inflate the count.
 */
export const usePresence = () => {
  const [count, setCount] = useState(0);
  const sidRef = useRef(getOrCreateSid());
  const beatTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const beat = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.post('/presence/heartbeat', { session_id: sidRef.current });
        if (alive && typeof data?.count === 'number') setCount(data.count);
      } catch { /* offline — silently retry */ }
    };

    const poll = async () => {
      try {
        const { data } = await api.get('/presence/count');
        if (alive && typeof data?.count === 'number') setCount(data.count);
      } catch { /* noop */ }
    };

    // Kick off immediately, then on interval
    beat();
    beatTimerRef.current = setInterval(beat, HEARTBEAT_MS);
    pollTimerRef.current = setInterval(poll, REFRESH_MS);

    const onVisible = () => { if (!document.hidden) beat(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive = false;
      clearInterval(beatTimerRef.current);
      clearInterval(pollTimerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return count;
};
